import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import client from '../../api/client'
import { useAuthStore } from '../../store/authStore'
import Spinner from '../../components/Spinner'
import { toast } from '../../components/Toast'

const MBTI_LIST = ['INTJ','INTP','ENTJ','ENTP','INFJ','INFP','ENFJ','ENFP','ISTJ','ISFJ','ESTJ','ESFJ','ISTP','ISFP','ESTP','ESFP']
const CATEGORIES = [{ key: 'love', label: '💕 연애' }, { key: 'friend', label: '👫 친구' }, { key: 'work', label: '💼 직장' }]

type CompatResult = {
  total_score: number
  saju_score: number
  mbti_score: number
  blood_score: number
  analysis: string
}

export default function Compatibility() {
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuthStore()
  const [category, setCategory] = useState('love')
  const [savePartner, setSavePartner] = useState(false)
  const [partners, setPartners] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<CompatResult | null>(null)

  const [target, setTarget] = useState({
    birth_date: '', birth_time: '', birth_type: 'solar',
    gender: 'F', mbti: '', blood_type: '',
    nickname: '',
  })

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return }
  }, [isAuthenticated])

  useEffect(() => {
    if (isAuthenticated) {
      client.get('/partners')
        .then(({ data }) => setPartners(data))
        .catch((e) => console.error('파트너 목록 로딩 실패:', e))
    }
  }, [isAuthenticated])

  const set = (k: string, v: string) => setTarget((t) => ({ ...t, [k]: v }))

  const handlePartnerSelect = (partner: any) => {
    setTarget({
      nickname: partner.nickname,
      birth_date: partner.birth_date,
      birth_time: partner.birth_time,
      birth_type: partner.birth_type,
      gender: partner.gender,
      mbti: partner.mbti,
      blood_type: partner.blood_type,
    })
  }

  const handleSubmit = async () => {
    if (!target.birth_date) { toast('상대방 생년월일을 입력해주세요.', 'error'); return }
    if (!target.nickname) { toast('상대방 이름을 입력해주세요.', 'error'); return }
    setLoading(true)
    setResult(null)
    try {
      if (savePartner) {
        await client.post('/partners', {
          nickname: target.nickname,
          birth_date: target.birth_date,
          birth_time: target.birth_time || null,
          birth_type: target.birth_type,
          gender: target.gender,
          mbti: target.mbti || null,
          blood_type: target.blood_type || null,
        })
        toast('상대방 정보가 저장되었습니다.', 'success')
      }

      const { data } = await client.post('/features/compatibility', {
        target: {
          birth_date: target.birth_date,
          birth_time: target.birth_time || null,
          birth_type: target.birth_type,
          gender: target.gender,
          mbti: target.mbti || null,
          blood_type: target.blood_type || null,
        },
        category,
      })
      setResult(data)
    } catch (e: any) {
      toast(e.response?.data?.detail || '궁합 분석에 실패했습니다.', 'error')
    } finally {
      setLoading(false)
    }
  }

  const inputCls = "w-full bg-[var(--color-bg)] border border-[var(--color-border-default)] rounded-lg px-3 py-2.5 text-[var(--color-text-primary)] text-sm focus:outline-none focus:border-[var(--color-border-accent)] transition-colors"
  const toggleBtn = (active: boolean) =>
    `flex-1 py-2 rounded-lg border text-xs font-medium transition-colors ${active ? 'bg-[var(--color-accent)] border-[var(--color-accent)] text-[var(--color-primary)] font-bold' : 'bg-[var(--color-bg)] border-[var(--color-border-default)] text-[var(--color-text-tertiary)] hover:border-[var(--color-border-accent)]'}`

  return (
    <div className="min-h-screen bg-[var(--color-bg)] p-4 sm:p-6">
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/dashboard')} className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-xl font-bold text-[var(--color-text-primary)]">💑 궁합 분석</h1>
        </div>

        {!result ? (
          <div className="space-y-4">
            {/* 나 vs 상대방 */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-xl p-4">
                <p className="text-[var(--color-text-tertiary)] text-xs mb-2">나</p>
                <p className="text-[var(--color-text-primary)] font-medium text-sm">{user?.nickname}</p>
                <p className="text-[var(--color-text-tertiary)] text-xs mt-1">{user?.mbti || 'MBTI 미입력'} · {user?.blood_type ? `${user.blood_type}형` : '혈액형 미입력'}</p>
              </div>
              <div className="bg-[var(--color-surface)] border border-[var(--color-accent)]/40 rounded-xl p-4">
                <p className="text-[var(--color-text-tertiary)] text-xs mb-2">상대방</p>
                <div className="flex gap-2 mb-2">
                  <select
                    className="flex-1 bg-transparent text-xs text-[var(--color-text-tertiary)] focus:outline-none cursor-pointer hover:text-[var(--color-text-primary)]"
                    value=""
                    onChange={(e) => {
                      const p = partners.find(item => item.id === parseInt(e.target.value))
                      if (p) handlePartnerSelect(p)
                    }}
                    disabled={partners.length === 0}
                  >
                    <option value="" disabled>저장된 상대방 선택...</option>
                    {partners.map(p => <option key={p.id} value={p.id}>{p.nickname}</option>)}
                  </select>
                </div>
                <input type="text" placeholder="이름 (별명)" className="w-full bg-transparent text-[var(--color-text-primary)] text-sm focus:outline-none placeholder-[var(--color-text-disabled)]"
                  value={target.nickname} onChange={(e) => set('nickname', e.target.value)} />
              </div>
            </div>

            {/* 상대방 정보 입력 */}
            <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-xl p-5 space-y-3">
              <h2 className="text-[var(--color-text-primary)] font-medium text-sm">상대방 정보 입력</h2>
              <div>
                <label className="block text-xs text-[var(--color-text-tertiary)] mb-1">생년월일 *</label>
                <input type="date" className={inputCls} value={target.birth_date} onChange={(e) => set('birth_date', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs text-[var(--color-text-tertiary)] mb-1">양력 / 음력</label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => set('birth_type', 'solar')} className={toggleBtn(target.birth_type === 'solar')}>양력</button>
                  <button type="button" onClick={() => set('birth_type', 'lunar')} className={toggleBtn(target.birth_type === 'lunar')}>음력</button>
                </div>
              </div>
              <div>
                <label className="block text-xs text-[var(--color-text-tertiary)] mb-1">태어난 시간 (선택)</label>
                <input type="time" className={inputCls} value={target.birth_time} onChange={(e) => set('birth_time', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs text-[var(--color-text-tertiary)] mb-1">성별</label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => set('gender', 'M')} className={toggleBtn(target.gender === 'M')}>남성</button>
                  <button type="button" onClick={() => set('gender', 'F')} className={toggleBtn(target.gender === 'F')}>여성</button>
                </div>
              </div>
              <div>
                <label className="block text-xs text-[var(--color-text-tertiary)] mb-1">MBTI (선택)</label>
                <select className={inputCls} value={target.mbti} onChange={(e) => set('mbti', e.target.value)}>
                  <option value="">선택 안함</option>
                  {MBTI_LIST.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-[var(--color-text-tertiary)] mb-1">혈액형 (선택)</label>
                <div className="flex flex-wrap gap-2">
                  {['', 'A', 'B', 'O', 'AB'].map((v) => (
                    <button key={v} type="button" onClick={() => set('blood_type', v)} className={toggleBtn(target.blood_type === v)}>
                      {v || '?'}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <input
                    type="checkbox"
                    id="savePartner"
                    className="w-4 h-4 accent-[var(--color-accent)]"
                    checked={savePartner}
                    onChange={(e) => setSavePartner(e.target.checked)}
                  />
                  <label htmlFor="savePartner" className="text-xs text-[var(--color-text-secondary)] cursor-pointer select-none">
                    상대방 정보 저장하기
                  </label>
                </div>
              </div>
            </div>

            {/* 카테고리 */}
            <div>
              <p className="text-[var(--color-text-tertiary)] text-sm mb-2">궁합 카테고리</p>
              <div className="flex gap-2">
                {CATEGORIES.map((c) => (
                  <button key={c.key} onClick={() => setCategory(c.key)}
                    className={`flex-1 py-2 rounded-xl text-sm font-medium border transition-colors ${category === c.key ? 'bg-[var(--color-accent)] border-[var(--color-accent)] text-[var(--color-primary)] font-bold' : 'bg-[var(--color-surface)] border-[var(--color-border-default)] text-[var(--color-text-tertiary)] hover:border-[var(--color-border-accent)]'}`}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            <button onClick={handleSubmit} disabled={loading}
              className="w-full py-3 bg-[var(--color-accent)] hover:opacity-90 disabled:opacity-60 text-[var(--color-primary)] rounded-xl font-bold transition-opacity flex items-center justify-center gap-2">
              {loading ? <><Spinner size="sm" /> 럭키가 살펴보는 중</> : '🔮 궁합 보기'}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* 결과 카드 */}
            <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-6">
              <div className="text-center mb-5">
                <p className="text-[var(--color-text-tertiary)] text-sm mb-1">{user?.nickname} 💕 {target.nickname || '상대방'}</p>
                <div className="text-5xl font-black text-[var(--color-accent)] mb-1">{result.total_score}점</div>
                <div className="text-[var(--color-accent)]">{'★'.repeat(Math.round(result.total_score / 20))}{'☆'.repeat(5 - Math.round(result.total_score / 20))}</div>
              </div>

              <div className="space-y-3 mb-5">
                {[
                  { label: '오행', score: result.saju_score },
                  { label: 'MBTI', score: result.mbti_score },
                  { label: '혈액형', score: result.blood_score },
                ].map((item) => (
                  <div key={item.label}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-[var(--color-text-tertiary)]">{item.label}</span>
                      <span className="text-[var(--color-text-secondary)]">{item.score}점</span>
                    </div>
                    <div className="bg-[var(--color-border-default)] rounded-full h-2">
                      <div className="bg-[var(--color-accent)] h-2 rounded-full transition-all" style={{ width: `${item.score}%` }} />
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-[var(--color-border-default)] pt-4">
                <p className="text-[var(--color-text-tertiary)] text-xs mb-2">🏪 운명편의점 분석</p>
                <p className="text-[var(--color-text-secondary)] text-sm leading-relaxed">{result.analysis}</p>
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setResult(null)}
                className="flex-1 py-3 border border-[var(--color-border-default)] text-[var(--color-text-tertiary)] rounded-xl text-sm hover:text-[var(--color-text-primary)] transition-colors">
                다시 보기
              </button>
              <button onClick={() => navigate('/counseling', { state: { autoStart: true, category: 'love', mode: 'chat' } })}
                className="flex-1 py-3 bg-[var(--color-accent)] hover:opacity-90 text-[var(--color-primary)] rounded-xl text-sm font-bold transition-opacity">
                💬 상담 연결
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
